import {
  ApolloClient,
  ApolloProvider,
  createHttpLink,
  from,
  InMemoryCache,
  Observable,
  QueryHookOptions,
  ServerError
} from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { onError } from '@apollo/client/link/error';
import useAuthToken from "../../hook/useAuthToken";
import { notifyError } from "../notification";
import sessionTimeout from "../timeout/timeout";

interface IFetchPolicyOption {
  NetworkOnly: QueryHookOptions;
  NoCache: QueryHookOptions;
}

export const FetchPolicyOption: IFetchPolicyOption = {
  NetworkOnly: { fetchPolicy: 'network-only' },
  NoCache: { fetchPolicy: 'no-cache' }
};

const Apollo = (props: any) => {
  const { children } = props

  const [getAccessToken] = useAuthToken();

  const authLink = setContext(async (_, { headers }) => {
    sessionTimeout.reset()

    const token = await getAccessToken()
    if (!token) {
      return {
        skip: true,
        headers: headers
      }
    } else {
      return {
        headers: {
          ...headers,
          Authorization: token ? `Bearer ${token}` : null,
          "X-OperationName": _.operationName ?? "" // pass the operation name as an optional http header so app insights can pick it up
        }
      }
    }
  })

  function extensionToString(extension: unknown) : string | undefined {
    if (!extension) return undefined;

    if (typeof extension === "string") return extension;
    
    return undefined;
  }

  const errorLink = onError(({ graphQLErrors, networkError, operation }) => {
    if (operation.getContext().skip) {
      return Observable.of(operation)
    }

    if (networkError) {
      if (networkError.name === 'ServerError') {
        const serverError = networkError as ServerError;

        if (serverError.statusCode === 401) {
          if (graphQLErrors && graphQLErrors.length > 0) {
            // try to get the error message code / correlation id from the graphql error response
            const errorMessageCode = extensionToString(graphQLErrors[0].extensions.errorMessageCode);
            const correlationId = extensionToString(graphQLErrors[0].extensions.correlationId);

            const searchParams = new URLSearchParams();
            if (errorMessageCode) searchParams.set("errorCode", errorMessageCode);
            if (correlationId) searchParams.set("correlationId", correlationId);

            // if we don't have the error code or correlation id, just redirect to the access denied page
            if (searchParams.size === 0) window.location.href = '/access-denied.html';
            else {
              // we have one or more values, append them to the query string
              window.location.href = `/access-denied.html?${searchParams.toString()}`;
            } 
          }
        }
        return;
      } else {
        notifyError(networkError.message);
      }
    }

    if (graphQLErrors) {
      // path through field validation error to UI
      if (graphQLErrors.some(error => error.extensions['field'])) {
        return
      }

      // only show distinct error messages
      const messages = new Set(
        graphQLErrors.map(error => {
          const correlationId = error.extensions?.correlationId;
          return correlationId? `${error.message}(Correlation ID: ${correlationId})`: error.message
        }))
      console.log('GraphQL error occurred in %s.', operation.operationName)

      messages.forEach((message) => notifyError(message))
    }

    return Observable.of(operation)
  })

  const httpLink = createHttpLink({
    uri: `${document.location.origin}/graphql`,
  })

  const client = new ApolloClient({
    link: from([errorLink, authLink, httpLink]),
    cache: new InMemoryCache(),
    connectToDevTools: true
  })

  return (
    <ApolloProvider client={client}>
      {children}
    </ApolloProvider>
  )
}

export default Apollo
